import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, Space, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetInsuranceTypeQuery,
  usePatchInsuranceTypeMutation,
  usePostInsuranceTypeMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {
  WorkshopInsuranceTypeForm,
  WorkshopInsuranceTypeFormSchema,
} from './types/WorkshopInsuranceTypeFormSchema';

export function WorkshopInsuranceTypeDetail() {
  const {id} = useParams();
  const navigate = useNavigate();

  const [postInsuranceType] = usePostInsuranceTypeMutation();
  const [patchInsuranceType] = usePatchInsuranceTypeMutation();

  const {
    data: insuranceType,
    isLoading,
    isError,
  } = useGetInsuranceTypeQuery({insuranceTypeId: id as string}, {skip: isNilOrEmpty(id)});

  const header: SettingsTemplateHeader = {
    title: id ? (insuranceType?.name ?? '') : i18n.t('entity.workshop.actions.newInsuranceType'),
    breadcrumbs: [
      {
        label: i18n.t('entity.workshop.labels.insuranceTypes'),
        href: settingsRoutes.workshopInsuranceTypes,
      },
    ],
  };

  const handleNavigateBack = () => {
    navigate(settingsRoutes.workshopInsuranceTypes);
  };

  const handleSubmit: FormSubmitHandler<WorkshopInsuranceTypeForm> = (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = id
      ? patchInsuranceType({
          insuranceTypeId: id,
          body: {
            name: formValues.name,
          },
        })
      : postInsuranceType({
          body: {
            name: formValues.name,
          },
        });

    return request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    name: insuranceType?.name,
  };

  const titleKey = id
    ? 'entity.workshop.labels.insuranceTypes'
    : 'entity.workshop.actions.newInsuranceType';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={header}
        isLoading={isLoading}
        isError={isError}
        isCreating={!id}
        data-testid={testIds.settings.workshopInsuranceTypesDetail('template')}
      >
        <Form<WorkshopInsuranceTypeForm>
          experimentalZodSchema={WorkshopInsuranceTypeFormSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <Box width={100}>
                <FormField
                  type="text"
                  name="name"
                  label={i18n.t('general.labels.name')}
                  control={control}
                  isRequired
                  data-testid={testIds.settings.workshopInsuranceTypesDetail('name')}
                />
              </Box>
              <Space fillAvailable />
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    title: id
                      ? i18n.t('general.actions.discardChanges')
                      : i18n.t('general.actions.discard'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.workshopInsuranceTypes),
                    'data-testid': testIds.settings.workshopInsuranceTypesDetail('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    variant: 'primary',
                    title: id
                      ? i18n.t('general.actions.saveChanges')
                      : i18n.t('general.actions.save'),
                    'data-testid': testIds.settings.workshopInsuranceTypesDetail('submit'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
